// API client for Analyzely backend

const API_BASE = import.meta.env.VITE_API_URL || '';

class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function getAuthToken(): Promise<string | null> {
  return localStorage.getItem('auth_token');
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    const code = data.code || 'UNKNOWN_ERROR';
    const message = data.error || data.message || 'An error occurred';

    // Handle token revoked or session expired - redirect to login
    // But NOT for feature/plan access errors (403 with specific codes)
    const planErrorCodes = ['FEATURE_NOT_AVAILABLE', 'SITE_LIMIT_REACHED', 'EVENT_NOT_AVAILABLE', 'NO_SUBSCRIPTION', 'DEMO_ACCOUNT_NOT_SUPPORTED'];
    if (response.status === 401 ||
        (response.status === 403 && !planErrorCodes.includes(code))) {
      // Clear local auth state
      localStorage.removeItem('auth_token');

      // Redirect to login with session expired message
      // Use replace to prevent back button returning to invalid state
      window.location.replace('/login?session_expired=true');

      // Throw error to stop any pending operations
      throw new ApiError(response.status, 'SESSION_EXPIRED', 'Session expired');
    }

    throw new ApiError(response.status, code, message);
  }

  return data;
}

// Auth API
export const authApi = {
  sendMagicLink: (email: string) =>
    request<{ success: boolean; message: string }>('/auth/send-magic-link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  getMe: () =>
    request<{ success: boolean; data: UserData }>('/auth/me'),

  refresh: () =>
    request<{ success: boolean; token: string }>('/auth/refresh', {
      method: 'POST',
    }),

  logout: () =>
    request<{ success: boolean }>('/auth/logout', {
      method: 'POST',
    }),

  updateProfile: (data: { first_name?: string; last_name?: string }) =>
    request<{ success: boolean; data: UserData }>('/auth/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteAccount: () =>
    request<{ success: boolean }>('/auth/me', {
      method: 'DELETE',
    }),
};

// Sites API
export const sitesApi = {
  list: () =>
    request<{ success: boolean; data: SiteData[] }>('/api/v1/sites'),

  get: (siteId: string) =>
    request<{ success: boolean; data: SiteDetailData }>(`/api/v1/sites/${siteId}`),

  sync: () =>
    request<{ success: boolean; data: SiteData[] }>('/api/v1/sites/sync'),

  delete: (siteId: string) =>
    request<{ success: boolean }>(`/api/v1/sites/${siteId}`, {
      method: 'DELETE',
    }),

  getStats: (siteId: string) =>
    request<{ success: boolean; data: SiteStats }>(`/api/v1/sites/${siteId}/stats`),

  disconnectGoogle: (siteId: string) =>
    request<{ success: boolean }>(`/api/v1/sites/${siteId}/disconnect-google`, {
      method: 'POST',
    }),
};

// Analytics API
export const analyticsApi = {
  get: (siteId: string) =>
    request<{ success: boolean; data: AnalyticsConfig | null }>(`/api/v1/analytics/${siteId}`),

  getAccounts: (siteId: string) =>
    request<{ success: boolean; data: GA4Account[] }>(`/api/v1/analytics/${siteId}/accounts`),

  getProperties: (siteId: string, accountId: string) =>
    request<{ success: boolean; data: GA4Property[] }>(
      `/api/v1/analytics/${siteId}/properties?accountId=${encodeURIComponent(accountId)}`
    ),

  getDataStreams: (siteId: string, propertyId: string) =>
    request<{ success: boolean; data: GA4DataStream[] }>(
      `/api/v1/analytics/${siteId}/datastreams?propertyId=${encodeURIComponent(propertyId)}`
    ),

  configure: (siteId: string, config: AnalyticsConfigInput) =>
    request<{ success: boolean; data: AnalyticsConfig }>(`/api/v1/analytics/${siteId}/configure`, {
      method: 'POST',
      body: JSON.stringify(config),
    }),

  applyCode: (siteId: string) =>
    request<{ success: boolean; data: { script_id: string; published: boolean } }>(
      `/api/v1/analytics/${siteId}/apply-code`,
      { method: 'POST' }
    ),

  removeCode: (siteId: string) =>
    request<{ success: boolean }>(`/api/v1/analytics/${siteId}/remove-code`, {
      method: 'POST',
    }),

  checkCode: (siteId: string) =>
    request<{
      success: boolean;
      data: {
        applied: boolean;
        script_id?: string;
        measurement_id?: string;
        message: string;
      };
    }>(`/api/v1/analytics/${siteId}/check-code`, {
      method: 'POST',
    }),

  enable: (siteId: string) =>
    request<{ success: boolean }>(`/api/v1/analytics/${siteId}/enable`, {
      method: 'POST',
    }),

  disable: (siteId: string) =>
    request<{ success: boolean }>(`/api/v1/analytics/${siteId}/disable`, {
      method: 'POST',
    }),

  getEvents: (siteId: string) =>
    request<{ success: boolean; data: { events: string[]; has_api_secret: boolean } }>(
      `/api/v1/analytics/${siteId}/events`
    ),

  updateEvents: (siteId: string, events: string[], apiSecret?: string) =>
    request<{ success: boolean; message: string; data: { events: string[]; has_api_secret: boolean; secret_auto_created?: boolean; code_reapplied?: boolean; published?: boolean } }>(
      `/api/v1/analytics/${siteId}/events`,
      {
        method: 'POST',
        body: JSON.stringify({ events, api_secret: apiSecret }),
      }
    ),

  createProperty: (
    siteId: string,
    accountId: string,
    propertyName: string,
    websiteUrl: string,
    timezone?: string,
    currency?: string
  ) =>
    request<{
      success: boolean;
      message: string;
      data: {
        property_id: string;
        property_name: string;
        measurement_id: string;
        data_stream_name: string;
        web_stream_id: string;
      };
    }>(`/api/v1/analytics/${siteId}/create-property`, {
      method: 'POST',
      body: JSON.stringify({
        account_id: accountId,
        property_name: propertyName,
        website_url: websiteUrl,
        timezone,
        currency,
      }),
    }),
};

// Reports API
export const reportsApi = {
  getStandard: (siteId: string, range = 'last7days', startDate?: string, endDate?: string) => {
    let url = `/api/v1/reports/${siteId}/standard?range=${range}`;
    if (startDate && endDate) {
      url += `&startDate=${startDate}&endDate=${endDate}`;
    }
    return request<{ success: boolean; data: StandardReportData }>(url);
  },

  getEcommerce: (siteId: string, range = 'last7days', startDate?: string, endDate?: string) => {
    let url = `/api/v1/reports/${siteId}/ecommerce?range=${range}`;
    if (startDate && endDate) {
      url += `&startDate=${startDate}&endDate=${endDate}`;
    }
    return request<{ success: boolean; data: EcommerceReportData }>(url);
  },

  getRealtime: (siteId: string) =>
    request<{ success: boolean; data: RealtimeReportData }>(`/api/v1/reports/${siteId}/realtime`),

  getOverview: (siteId: string) =>
    request<{ success: boolean; data: OverviewData }>(`/api/v1/reports/${siteId}/overview`),

  getTrafficSources: (siteId: string, range = 'last7days', startDate?: string, endDate?: string) => {
    let url = `/api/v1/reports/${siteId}/traffic-sources?range=${range}`;
    if (startDate && endDate) {
      url += `&startDate=${startDate}&endDate=${endDate}`;
    }
    return request<{ success: boolean; data: TrafficSourcesReportData }>(url);
  },

  getHourlyAnalysis: (siteId: string, range = 'last7days', startDate?: string, endDate?: string) => {
    let url = `/api/v1/reports/${siteId}/hourly-analysis?range=${range}`;
    if (startDate && endDate) {
      url += `&startDate=${startDate}&endDate=${endDate}`;
    }
    return request<{ success: boolean; data: HourlyAnalysisReportData }>(url);
  },

  getPagesAnalysis: (siteId: string, range = 'last7days', startDate?: string, endDate?: string) => {
    let url = `/api/v1/reports/${siteId}/pages-analysis?range=${range}`;
    if (startDate && endDate) {
      url += `&startDate=${startDate}&endDate=${endDate}`;
    }
    return request<{ success: boolean; data: PagesAnalysisReportData }>(url);
  },
};

// Google API
export const googleApi = {
  getStatus: (siteId: string) =>
    request<{ success: boolean; data: { connected: boolean; email?: string } }>(
      `/google/status/${siteId}`
    ),

  disconnect: (siteId: string) =>
    request<{ success: boolean }>(`/google/disconnect/${siteId}`, {
      method: 'POST',
    }),

  getConnectUrl: (siteId: string) => {
    const token = localStorage.getItem('auth_token');
    return `${API_BASE}/google/connect/${siteId}?token=${token}`;
  },
};

// Webflow API
export const webflowApi = {
  // Get Webflow OAuth authorize URL - redirects to Webflow to sync sites
  getAuthorizeUrl: () => {
    const token = localStorage.getItem('auth_token');
    return `${API_BASE}/webflow/auth?token=${token}`;
  },
};

// Billing API
export const billingApi = {
  getPlans: () =>
    request<{ success: boolean; data: PlanData[] }>('/api/v1/billing/plans'),

  getSubscription: () =>
    request<{ success: boolean; data: SubscriptionData }>('/api/v1/billing/subscription'),

  getUsage: () =>
    request<{ success: boolean; data: UsageLimitsData }>('/api/v1/billing/usage'),

  createCheckout: (planId: string, interval: 'monthly' | 'yearly') =>
    request<{ success: boolean; data: { checkout_url: string; session_id: string } }>(
      '/api/v1/billing/checkout',
      {
        method: 'POST',
        body: JSON.stringify({ plan_id: planId, interval }),
      }
    ),

  getPortalUrl: () =>
    request<{ success: boolean; data: { portal_url: string } }>(
      '/api/v1/billing/portal',
      { method: 'POST' }
    ),

  cancelSubscription: () =>
    request<{ success: boolean; message: string }>('/api/v1/billing/cancel', {
      method: 'POST',
    }),

  checkFeature: (feature: string) =>
    request<{ success: boolean; data: { allowed: boolean; reason?: string; current_plan: string } }>(
      '/api/v1/billing/check-feature',
      {
        method: 'POST',
        body: JSON.stringify({ feature }),
      }
    ),

  // Sync subscription state from Stripe (ensures our data matches Stripe even if webhooks fail)
  syncSubscription: () =>
    request<{ success: boolean; data: { synced: boolean; changes?: Record<string, unknown> | null; reason?: string } }>(
      '/api/v1/billing/sync',
      { method: 'POST' }
    ),
};

// Types
export interface UserData {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  is_designer?: boolean;
  created_at: string;
}

export interface SiteData {
  id: string;
  user_id: string;
  webflow_site_id: string;
  display_name: string;
  short_name: string;
  preview_url?: string;
  is_authorized: boolean;
  analytics_enabled: boolean;
  has_ga4_property: boolean;
  measurement_id?: string;
  google_connected: boolean;
  created_at: string;
  updated_at: string;
}

export interface SiteDetailData extends SiteData {
  analytics: {
    enabled: boolean;
    property_id?: string;
    measurement_id?: string;
    data_stream_name?: string;
    custom_code_applied: boolean;
    events?: string[];
  } | null;
  google_connected: boolean;
  google_email?: string;
}

export interface SiteStats {
  site_id: string;
  display_name: string;
  analytics_enabled: boolean;
  has_ga4_property: boolean;
  custom_code_applied: boolean;
  created_at: string;
}

export interface AnalyticsConfig {
  id: string;
  site_id: string;
  google_account_id?: string;
  property_id?: string;
  measurement_id?: string;
  data_stream_name?: string;
  web_stream_id?: string;
  enabled: boolean;
  custom_code_applied: boolean;
  events?: string[];
  created_at: string;
  updated_at: string;
}

export interface AnalyticsConfigInput {
  property_id: string;
  measurement_id: string;
  data_stream_name: string;
  web_stream_id?: string;
}

export interface GA4Account {
  name: string;
  displayName: string;
}

export interface GA4Property {
  name: string;
  displayName: string;
  propertyType: string;
  parent: string;
}

export interface GA4DataStream {
  name: string;
  displayName: string;
  webStreamData?: {
    measurementId: string;
    defaultUri: string;
  };
}

export interface StandardReportData {
  sessions: number;
  users: number;
  pageViews: number;
  bounceRate: number;
  avgSessionDuration: number;
  newUsers: number;
  sessionsPerUser: number;
  engagedSessions: number;
  sessionsOverTime: Array<{ date: string; value: number }>;
  topPages: Array<{ dimension: string; value: number; percentage?: number }>;
  topReferrers: Array<{ dimension: string; value: number; percentage?: number }>;
  topCountries: Array<{ dimension: string; value: number; percentage?: number }>;
  deviceCategories: Array<{ dimension: string; value: number; percentage?: number }>;
  browsers: Array<{ dimension: string; value: number; percentage?: number }>;
  operatingSystems: Array<{ dimension: string; value: number; percentage?: number }>;
  previousPeriod?: {
    sessions: number;
    users: number;
    pageViews: number;
    bounceRate: number;
    avgSessionDuration: number;
    sessionsPerUser: number;
    engagedSessions: number;
    sessionsOverTime?: Array<{ date: string; value: number }>;
  };
  dateRange: {
    startDate: string;
    endDate: string;
    preset: string;
  };
  property: {
    id: string;
    measurementId: string;
  };
}

export interface EcommerceReportData {
  totalRevenue: number;
  transactions: number;
  averageOrderValue: number;
  conversionRate: number;
  addToCarts: number;
  checkouts: number;
  itemViews: number;
  cartAbandonmentRate: number;
  sessions: number;
  revenueOverTime: Array<{ date: string; value: number }>;
  topProducts: Array<{ name: string; revenue: number; quantity: number; percentage: number }>;
  previousPeriod?: {
    totalRevenue: number;
    transactions: number;
    averageOrderValue: number;
    addToCarts: number;
    checkouts: number;
    itemViews: number;
    sessions: number;
  };
  currency: string;
  dateRange: {
    startDate: string;
    endDate: string;
    preset: string;
  };
  property: {
    id: string;
    measurementId: string;
  };
}

export interface RealtimeReportData {
  activeUsers: number;
  pageViews: number;
  events: number;
  topPages: Array<{ dimension: string; value: number; percentage?: number }>;
  topCountries: Array<{ dimension: string; value: number; percentage?: number }>;
  deviceCategories: Array<{ dimension: string; value: number; percentage?: number }>;
  timestamp: string;
}

export interface OverviewData {
  sessions: { value: number; change: number };
  users: { value: number; change: number };
  pageViews: { value: number; change: number };
  bounceRate: { value: number; formatted: string };
  avgSessionDuration: { value: number; formatted: string };
  dateRange: { startDate: string; endDate: string };
}

// Traffic Sources Report types
export interface TrafficSourcesReportData {
  bySource: TrafficSourceData[];
  byMedium: Array<{ dimension: string; value: number; percentage?: number }>;
  campaigns: CampaignData[];
  totalSessions: number;
  previousPeriod?: {
    totalSessions: number;
  };
  dateRange: {
    startDate: string;
    endDate: string;
    preset: string;
  };
  property: {
    id: string;
    measurementId: string;
  };
}

export interface TrafficSourceData {
  source: string;
  sessions: number;
  users: number;
  bounceRate: number;
  percentage: number;
}

export interface CampaignData {
  campaign: string;
  sessions: number;
  users: number;
  bounceRate: number;
  percentage: number;
}

// Hourly Analysis Report types
export interface HourlyAnalysisReportData {
  heatmapData: HeatmapCell[][];
  peakHour: { hour: number; dayOfWeek: number; value: number };
  busiestDay: { dayOfWeek: number; totalValue: number; dayName: string };
  totalSessions: number;
  dateRange: {
    startDate: string;
    endDate: string;
    preset: string;
  };
  property: {
    id: string;
    measurementId: string;
  };
}

export interface HeatmapCell {
  dayOfWeek: number;
  hour: number;
  value: number;
  intensity: number;
}

// Pages Analysis Report types
export interface PagesAnalysisReportData {
  landingPages: LandingPageData[];
  exitPages: ExitPageData[];
  totalSessions: number;
  totalExits: number;
  previousPeriod?: {
    totalSessions: number;
    totalExits: number;
  };
  dateRange: {
    startDate: string;
    endDate: string;
    preset: string;
  };
  property: {
    id: string;
    measurementId: string;
  };
}

export interface LandingPageData {
  page: string;
  sessions: number;
  users: number;
  bounceRate: number;
  avgSessionDuration: number;
  percentage: number;
}

export interface ExitPageData {
  page: string;
  exits: number;
  pageViews: number;
  exitRate: number;
  percentage: number;
}

// Billing types
export type FeatureFlag =
  | 'standard_reports'
  | 'email_support'
  | 'ecommerce_reports'
  | 'realtime_analytics'
  | 'custom_events'
  | 'priority_support'
  | 'white_label'
  | 'api_access'
  | 'team_collaboration';

export type SubscriptionStatus = 'active' | 'trialing' | 'past_due' | 'cancelled' | 'incomplete';
export type BillingInterval = 'monthly' | 'yearly';
export type EcommerceEventType = 'purchase' | 'add_to_cart' | 'checkout' | 'view_item' | 'view_item_list';

export interface PlanData {
  id: string;
  name: string;
  monthly_price: number;
  yearly_price: number;
  max_sites: number;
  data_retention_days: number;
  features: string[];
  ecommerce_events: EcommerceEventType[];
  created_at: string;
  updated_at: string;
}

export interface SubscriptionData {
  id: string;
  user_id: string;
  plan_id: string;
  stripe_subscription_id?: string;
  stripe_customer_id?: string;
  status: SubscriptionStatus;
  billing_interval: BillingInterval;
  current_period_end?: string;
  trial_ends_at?: string;
  cancel_at_period_end: boolean;
  is_grandfathered: boolean;
  grandfathered_sites_count?: number;
  created_at: string;
  updated_at: string;
  plan: PlanData;
  trial_days_remaining?: number | null;
}

export interface UsageLimitsData {
  sites: {
    used: number;
    limit: number;
    canAddMore: boolean;
  };
  dataRetentionDays: number;
  features: string[];
}

export { ApiError };
